import React, { useMemo, useState } from "react";
import { Clock, Search, CalendarClock } from "lucide-react";
import { Spinner, EmptyState } from "../../components/common";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { fmtINR, fmtTimeIST } from "../../lib/api";
import { PStatus, VehicleTypeBadge, fmtMinutes } from "../../components/parking/common";
import { ReceiptDialog } from "../../components/parking/Ticket";
import { PostingStrip } from "../../components/layouts/MarshalLayout";
import { useMarshal, useMarshalDashboard, estimateDue } from "./marshalShared";
import { BookingSheet } from "./BookingSheet";

/* Phase 202 — Inside: every vehicle currently parked (soonest to leave first, overstays on top) + upcoming reservations. */

export default function MarshalInside() {
  const { me } = useMarshal();
  const { dash, refresh } = useMarshalDashboard();
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const rows = useMemo(() => {
    if (!dash) return [];
    const s = q.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "");
    const list = [...dash.active].sort((a, b) => (b.overstay_minutes || 0) - (a.overstay_minutes || 0) || a.end_at.localeCompare(b.end_at));
    return s ? list.filter((b) => b.vehicle_number.includes(s) || b.booking_no.includes(s)) : list;
  }, [dash, q]);
  const upcoming = useMemo(() => {
    if (!dash) return [];
    const s = q.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "");
    return s ? dash.upcoming.filter((b) => b.vehicle_number.includes(s) || b.booking_no.includes(s)) : dash.upcoming;
  }, [dash, q]);
  if (!dash) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="marshal-inside">
      <PostingStrip location={dash.location} shift={dash.shift_now} occupancy={dash.occupancy} />
      <div className="relative"><Search className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by vehicle or booking no" className="h-11 pl-9 font-mono" data-testid="inside-filter" /></div>

      <section>
        <p className="text-sm font-semibold font-heading mb-2 flex items-center justify-between">Inside now <Badge variant="secondary" data-testid="inside-count">{rows.length}</Badge></p>
        {rows.length === 0 ? <EmptyState title="No vehicle inside" subtitle="Checked-in vehicles appear here with their time left." /> : (
          <div className="space-y-2" data-testid="inside-list">
            {rows.map((b) => {
              const due = estimateDue(b, me);
              const over = b.overstay_minutes > 0;
              return (
                <button key={b.id} type="button" onClick={() => setSheet({ booking: b })} data-testid={`inside-row-${b.booking_no}`}
                  className={`w-full text-left rounded-xl border bg-card px-3 py-2.5 shadow-sm hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${over ? "border-destructive/40" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 min-w-0"><VehicleTypeBadge type={b.vehicle_type} short /><span className="font-mono font-semibold truncate">{b.vehicle_number}</span>{b.kind === "pass" && <Badge variant="outline" className="text-[10px]">Pass</Badge>}</span>
                    <span className={`text-xs font-semibold shrink-0 ${over ? "text-destructive" : b.ends_in_minutes <= 10 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--success))]"}`} data-testid={`inside-time-${b.booking_no}`}><Clock className="h-3 w-3 inline mr-1" />{over ? `Over ${fmtMinutes(b.overstay_minutes)}` : `${fmtMinutes(Math.max(b.ends_in_minutes, 0))} left`}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 text-xs text-muted-foreground">
                    <span><span className="font-mono">{b.booking_no}</span> · in {fmtTimeIST(b.checked_in_at)} · till {fmtTimeIST(b.end_at)}</span>
                    <span className={due > 0 ? "text-destructive font-semibold" : ""}>{due > 0 ? `Due ${fmtINR(due)}` : "Paid"}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <p className="text-sm font-semibold font-heading mb-2 flex items-center justify-between"><span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-primary" /> Upcoming reservations</span><Badge variant="secondary" data-testid="upcoming-count">{upcoming.length}</Badge></p>
        {upcoming.length === 0 ? <p className="text-sm text-muted-foreground px-1">No app reservations pending arrival.</p> : (
          <div className="space-y-2" data-testid="upcoming-list">
            {upcoming.map((b) => (
              <button key={b.id} type="button" onClick={() => setSheet({ booking: b })} data-testid={`upcoming-row-${b.booking_no}`}
                className="w-full text-left rounded-xl border bg-card px-3 py-2.5 shadow-sm hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 min-w-0"><VehicleTypeBadge type={b.vehicle_type} short /><span className="font-mono font-semibold truncate">{b.vehicle_number}</span></span>
                  <PStatus status={b.payment_status} />
                </div>
                <p className="text-xs text-muted-foreground mt-1"><span className="font-mono">{b.booking_no}</span> · {fmtTimeIST(b.start_at)} → {fmtTimeIST(b.end_at)} · {fmtINR(b.amount)}{b.citizen_name ? ` · ${b.citizen_name}` : ""}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {sheet && <BookingSheet booking={sheet.booking} onClose={() => setSheet(null)} onDone={() => { setSheet(null); refresh(); }} onViewReceipt={setReceipt} />}
      <ReceiptDialog receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
